/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/settingsHelpers.ts (Queues)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper utilities for managing Queue settings.

────────────────────────────────────────────────────────────────*/

import { FormState } from '../types';
import { KNOWN_SETTINGS_KEYS } from '../constants';
import { combineDestination } from './destinationHelpers';

/**
 * Extract extra settings (unknown keys) from settings object
 */
export function extractExtraSettings(settings: Record<string, unknown>): Record<string, unknown> {
  const extraSettings: Record<string, unknown> = {};
  Object.entries(settings).forEach(([key, value]) => {
    if (!KNOWN_SETTINGS_KEYS.has(key)) {
      extraSettings[key] = value;
    }
  });
  return extraSettings;
}

/**
 * Get string setting with fallback
 */
export function getStringSetting(settings: Record<string, unknown>, key: string, fallback = ''): string {
  const value = settings[key];
  return typeof value === 'string' ? value : fallback;
}

/**
 * Get boolean setting with fallback
 */
export function getBooleanSetting(settings: Record<string, unknown>, key: string, fallback = false): boolean {
  const value = settings[key];
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  if (typeof value === 'number') return value === 1;
  return fallback;
}

/**
 * Get number setting with fallback
 */
export function getNumberSetting(settings: Record<string, unknown>, key: string, fallback = 0): number {
  const value = settings[key];
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

/**
 * Build settings object from form state
 */
export function buildSettingsFromFormState(formState: FormState): Record<string, unknown> {
  const settings: Record<string, unknown> = {};

  // General
  settings.time_base_score = formState.timeBaseScore;
  settings.time_base_score_seconds = Number(formState.timeBaseScoreSeconds) || 0;
  settings.max_wait_time_with_no_agent = Number(formState.maxWaitTimeWithNoAgent) || 300;
  if (formState.maxWaitTimeWithNoAgentTimeReached) {
    settings.max_wait_time_with_no_agent_time_reached = formState.maxWaitTimeWithNoAgentTimeReached;
  }

  // Tier & Agent Rules
  settings.tier_rules_apply = formState.tierRulesApply;
  settings.tier_rule_wait = Number(formState.tierRuleWait) || 0;
  settings.tier_rule_multiply_level = formState.tierRuleMultiplyLevel;
  settings.tier_rule_no_agent_no_wait = formState.tierRuleNoAgentNoWait;

  // Announcements
  if (formState.welcomeSound) {
    settings.welcome_sound = formState.welcomeSound;
  }
  if (formState.exitSound) {
    settings.exit_sound = formState.exitSound;
  }
  if (formState.agentConnectSound) {
    settings.agent_connect_sound = formState.agentConnectSound;
  }
  settings.service_level_seconds = Number(formState.serviceLevelSeconds) || 0;
  if (formState.announceSound) {
    settings.announce_sound = formState.announceSound;
  }
  settings.announce_frequency = Number(formState.announceFrequency) || 0;
  const timeoutDestination = combineDestination(formState.timeoutDestinationModule, formState.timeoutDestinationDestination);
  if (timeoutDestination) {
    settings.timeout_destination = timeoutDestination;
  }

  // Caller Experience
  settings.discard_abandoned_after = Number(formState.discardAbandonedAfter) || 60;
  settings.abandoned_resume_allowed = formState.abandonedResumeAllowed;
  if (formState.callerIdNamePrefix) {
    settings.caller_id_name_prefix = formState.callerIdNamePrefix;
  }
  if (formState.callerIdNumberPrefix) {
    settings.caller_id_number_prefix = formState.callerIdNumberPrefix;
  }
  if (formState.callerExitKey && /^[0-9*#]$/.test(formState.callerExitKey)) {
    settings.caller_exit_key = formState.callerExitKey;
  }

  // Wrap-up Time, Queue Priority, and After Agent Hangup Destination
  settings.wrap_up_time = Number(formState.wrapUpTime) || 10;
  settings.cc_priority = Number(formState.queuePriority) || 0;
  settings.announce_hold_time = formState.announceHoldTime;
  settings.announce_position = formState.announcePosition;
  const executeOnHangup = combineDestination(formState.afterAgentHangupDestinationModule, formState.afterAgentHangupDestinationDestination);
  if (executeOnHangup) {
    settings.execute_on_hangup = executeOnHangup;
  }

  // Advanced
  settings.max_wait_agents = Number(formState.maxWaitAgents) || 0;
  settings.max_no_answer = Number(formState.maxNoAnswer) || 3;
  settings.cc_max_wait_size = Number(formState.queueMaxSize) || 0;
  settings.agent_no_answer_delay_time = Number(formState.agentNoAnswerDelay) || 0;
  settings.announce_estimated_holdtime = formState.announceEstimatedHoldTime;

  return settings;
}

